(() => {
  const PROXY_ENDPOINT = '/apps/asf-b2b/prices';
  const LINE_SELECTOR = '[data-asf-cart-line][data-asf-cart-variant-id]';
  const UNIT_SELECTOR = '[data-asf-cart-unit-price]';
  const TOTAL_SELECTOR = '[data-asf-cart-line-total]';
  const REQUEST_DELAY = 80;

  let requestToken = 0;
  let scheduledRefresh = null;

  function formatPrice(value) {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return '';
    }

    return (
      new Intl.NumberFormat('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numericValue) + ' lei'
    );
  }

  function getLines() {
    return Array.from(
      document.querySelectorAll(LINE_SELECTOR)
    );
  }

  function getQuantity(line) {
    const quantityInput = line.querySelector(
      '.quantity__input'
    );

    const quantity = Number(
      quantityInput?.value ||
        line.dataset.asfCartQuantity
    );

    return Number.isFinite(quantity)
      ? quantity
      : 1;
  }

  function rememberOriginal(element) {
    if (
      element &&
      !element.dataset.asfOriginalHtml
    ) {
      element.dataset.asfOriginalHtml =
        element.innerHTML;
      element.dataset.asfOriginalClass =
        element.className;
    }
  }

  function rememberLine(line) {
    line.querySelectorAll(UNIT_SELECTOR).forEach(
      rememberOriginal
    );

    line.querySelectorAll(TOTAL_SELECTOR).forEach(
      rememberOriginal
    );
  }

  function resetElement(element) {
    if (
      element?.dataset.asfOriginalHtml
    ) {
      element.innerHTML =
        element.dataset.asfOriginalHtml;
      element.className =
        element.dataset.asfOriginalClass || '';
    }
  }

  function resetLine(line) {
    line.classList.remove('asf-b2b-price-applied');

    line.querySelectorAll(UNIT_SELECTOR).forEach(
      (element) => {
        resetElement(element);
      }
    );

    line.querySelectorAll(TOTAL_SELECTOR).forEach(
      resetElement
    );
  }

  function hasSpecialPrice(priceData) {
    const shopifyPrice = Number(
      priceData.shopify_price
    );

    const finalPrice = Number(
      priceData.final_price
    );

    return (
      priceData.has_special_price === true &&
      Number.isFinite(shopifyPrice) &&
      Number.isFinite(finalPrice) &&
      shopifyPrice !== finalPrice
    );
  }

  function buildUnitHtml(priceData) {
    const finalPrice = Number(
      priceData.final_price
    );

    if (!Number.isFinite(finalPrice)) {
      return '';
    }

    if (!hasSpecialPrice(priceData)) {
      return formatPrice(finalPrice);
    }

    return [
      '<span class="visually-hidden">Pret standard</span>',
      '<s class="cart-item__old-price product-option">',
      formatPrice(priceData.shopify_price),
      '</s>',
      '<span class="visually-hidden">Pret B2B</span>',
      '<strong class="cart-item__final-price product-option">',
      formatPrice(finalPrice),
      '</strong>',
    ].join('');
  }

  function buildLineHtml(priceData, quantity) {
    const finalPrice = Number(
      priceData.final_price
    );

    if (!Number.isFinite(finalPrice)) {
      return '';
    }

    const linePrice = finalPrice * quantity;

    if (!hasSpecialPrice(priceData)) {
      return (
        '<span class="price price--end">' +
        formatPrice(linePrice) +
        '</span>'
      );
    }

    return [
      '<div class="cart-item__discounted-prices">',
      '<span class="visually-hidden">Pret standard</span>',
      '<s class="cart-item__old-price price price--end">',
      formatPrice(
        Number(priceData.shopify_price) * quantity
      ),
      '</s>',
      '<span class="visually-hidden">Pret B2B</span>',
      '<span class="price price--end">',
      formatPrice(linePrice),
      '</span>',
      '</div>',
    ].join('');
  }

  function applyLinePrice(line, priceData) {
    const finalPrice = Number(
      priceData?.final_price
    );

    if (!Number.isFinite(finalPrice)) {
      resetLine(line);
      return;
    }

    const quantity = getQuantity(line);
    const unitHtml = buildUnitHtml(priceData);
    const lineHtml = buildLineHtml(
      priceData,
      quantity
    );
    const isSpecial = hasSpecialPrice(priceData);

    line.classList.add('asf-b2b-price-applied');

    line.querySelectorAll(UNIT_SELECTOR).forEach(
      (element) => {
        element.className =
          element.dataset.asfOriginalClass ||
          element.className;

        if (isSpecial) {
          element.classList.add(
            'cart-item__discounted-prices'
          );
        } else {
          element.classList.remove(
            'cart-item__discounted-prices'
          );
          element.classList.add('product-option');
        }

        element.innerHTML = unitHtml;
      }
    );

    line.querySelectorAll(TOTAL_SELECTOR).forEach(
      (element) => {
        element.innerHTML = lineHtml;
      }
    );
  }

  async function refreshCartLinePrices() {
    const lines = getLines();

    if (lines.length === 0) {
      return;
    }

    lines.forEach(rememberLine);

    const variantIds = Array.from(
      new Set(
        lines
          .map((line) => line.dataset.asfCartVariantId)
          .filter(Boolean)
      )
    );

    if (variantIds.length === 0) {
      return;
    }

    const currentToken = ++requestToken;

    try {
      const response = await fetch(
        PROXY_ENDPOINT +
          '?variant_ids=' +
          encodeURIComponent(
            variantIds.join(',')
          ),
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          'Price proxy returned HTTP ' +
            response.status
        );
      }

      const result = await response.json();

      if (currentToken !== requestToken) {
        return;
      }

      if (
        !result ||
        result.authenticated !== true ||
        !Array.isArray(result.prices)
      ) {
        lines.forEach(resetLine);
        return;
      }

      const pricesByVariant = new Map();

      result.prices.forEach((priceItem) => {
        pricesByVariant.set(
          String(priceItem.variant_id),
          priceItem
        );
      });

      lines.forEach((line) => {
        const priceData = pricesByVariant.get(
          String(line.dataset.asfCartVariantId)
        );

        if (priceData) {
          applyLinePrice(line, priceData);
        } else {
          resetLine(line);
        }
      });
    } catch (error) {
      console.error(
        'ASF cart line price loading error:',
        error
      );

      lines.forEach(resetLine);
    }
  }

  function scheduleRefresh() {
    window.clearTimeout(scheduledRefresh);

    scheduledRefresh = window.setTimeout(
      refreshCartLinePrices,
      REQUEST_DELAY
    );
  }

  function watchCartChanges() {
    const observer = new MutationObserver(
      (mutations) => {
        const hasCartChange = mutations.some(
          (mutation) =>
            Array.from(mutation.addedNodes).some(
              (node) =>
                node.nodeType === 1 &&
                (node.matches?.(LINE_SELECTOR) ||
                  node.querySelector?.(
                    LINE_SELECTOR
                  ))
            )
        );

        if (hasCartChange) {
          scheduleRefresh();
        }
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      scheduleRefresh();
      watchCartChanges();

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          scheduleRefresh
        );
      }
    },
    { once: true }
  );
})();
